import type { GlobalSearchResult, ModuleSearchAdapter } from "../types";
import { getSearchClient } from "../apiClient";

interface PlannerEvent {
  // Backend's normalize_event_dict puts the iCal UID in `id` (not `uid`); the
  // older shape this adapter used to assume never matched the wire response.
  calendar_id: string;
  id: string;
  title?: string | null;
  description?: string | null;
  location?: string | null;
  start: string;
  all_day?: boolean;
}

interface PlannerSearchResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PlannerEvent[];
}

function formatWhen(iso: string, allDay?: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (allDay) {
    return d.toLocaleDateString(undefined, { dateStyle: "medium" });
  }
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function mapEvent(e: PlannerEvent): GlobalSearchResult {
  const parts: string[] = [formatWhen(e.start, e.all_day)];
  if (e.location) parts.push(e.location);
  // URL-encode the uid: iCal UIDs commonly contain '@' and can contain other
  // reserved characters that would otherwise corrupt the path segment.
  const encodedUid = encodeURIComponent(e.id);
  return {
    id: `calendar:${e.calendar_id}:${e.id}`,
    moduleId: "calendar",
    title: e.title && e.title.length > 0 ? e.title : "(Untitled event)",
    subtitle: parts.join(" · "),
    timestamp: e.start,
    href: `/planner/event/${e.calendar_id}/${encodedUid}`,
  };
}

export const calendarAdapter: ModuleSearchAdapter = {
  moduleId: "calendar",
  label: "Planner",
  async search(q, signal) {
    const res = await getSearchClient().get<PlannerSearchResponse>(
      `/api/calendar/calendars/events/search/?q=${encodeURIComponent(
        q,
      )}&page_size=3`,
      { signal },
    );
    return res.results.map(mapEvent);
  },
};
